import { Field } from '@lmt/unicorn-shared/app/components/common/Field';
import { Input } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/Input';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import clsx from 'clsx';
import React, { useEffect, useMemo, useRef } from 'react';

import styles from '@lmt/unicorn-shared/src/app/components/privileged-mode/TotpSixDigitsInput.module.scss';

type Props = {
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
  onEnter?: () => void;
  className?: string;
  errorText?: string;
};

const OTP_LENGTH = 6;
/**
 * We want each cell to be in "error" visual state (red border),
 * but the actual error message should be rendered only once by <Field>.
 * Input shows error styling only when `errorText` is set, so we pass a non-breaking space.
 */
const SILENT_ERROR = '\u00A0';

/** Keep only digits and cut to max length. */
function normalizeDigits(input: string, maxLength = OTP_LENGTH): string {
  return (input ?? '').replace(/\D/g, '').slice(0, maxLength);
}

/** Convert string value into a fixed-length array of single digits ('' for empty). */
function splitIntoOtpDigits(value: string, length = OTP_LENGTH): string[] {
  const digitsOnly = normalizeDigits(value);
  const result: string[] = [];

  for (let i = 0; i < length; i++) {
    result.push(digitsOnly[i] ?? '');
  }

  return result;
}

function getFirstEmptyIndex(digits: string[], length = OTP_LENGTH): number {
  const emptyIndex = digits.findIndex((d) => d === '');
  return emptyIndex === -1 ? length - 1 : emptyIndex;
}

/** Clamp index into [0..length-1]. */
function normalizeIndex(index: number, length = OTP_LENGTH): number {
  return Math.max(0, Math.min(length - 1, index));
}

export const TotpSixDigitsInput: React.FC<Props> = ({
  value,
  onChange,
  disabled,
  onEnter,
  className,
  errorText,
}) => {
  const i18n = useScopedTranslation('privileged_mode');
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);
  const digits = useMemo(() => splitIntoOtpDigits(value), [value]);

  const focusCell = (index: number, options?: { select?: boolean }) => {
    const safeIndex = normalizeIndex(index, OTP_LENGTH);
    const input = inputRefs.current[safeIndex];
    if (!input) return;

    input.focus();

    if (options?.select) {
      requestAnimationFrame(() => input.select?.());
    }
  };

  // Replace digit at position and make new combined value
  const setDigitAt = (index: number, digitChar: string) => {
    const nextDigits = [...digits];
    nextDigits[index] = digitChar;
    onChange(nextDigits.join(''));
  };

  const setDigitsFromIndex = (startIndex: number, text: string) => {
    const pastedDigits = normalizeDigits(text);
    if (!pastedDigits) return;

    const nextDigits = [...digits];
    for (
      let k = 0;
      k < pastedDigits.length && startIndex + k < OTP_LENGTH;
      k++
    ) {
      nextDigits[startIndex + k] = pastedDigits[k];
    }
    onChange(nextDigits.join(''));

    const focusIndex = getFirstEmptyIndex(nextDigits);
    focusCell(focusIndex, { select: true });
  };

  useEffect(() => {
    focusCell(0, { select: true });
  }, []);

  useEffect(() => {
    if (!errorText) return;
    const idx = getFirstEmptyIndex(digits);
    focusCell(idx, { select: true });
  }, [errorText, digits]);

  const handleKeyDown =
    (i: number) => (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (disabled) return;

      // Enter
      if (e.key === 'Enter') {
        e.preventDefault();
        onEnter?.();
        return;
      }

      // Navigation
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        focusCell(i - 1, { select: true });
        return;
      }
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        focusCell(i + 1, { select: true });
        return;
      }

      if (e.key === 'Backspace') {
        e.preventDefault();

        if (digits[i]) {
          setDigitAt(i, '');
          focusCell(i, { select: true });
        } else {
          const prev = Math.max(i - 1, 0);
          if (digits[prev]) setDigitAt(prev, '');
          focusCell(prev, { select: true });
        }
        return;
      }

      if (e.key === 'Delete') {
        e.preventDefault();
        setDigitAt(i, '');
        focusCell(i, { select: true });
        return;
      }

      if (/^\d$/.test(e.key)) {
        e.preventDefault();
        setDigitAt(i, e.key);
        focusCell(Math.min(i + 1, OTP_LENGTH - 1), { select: true });
        return;
      }

      const allowed =
        e.key === 'Tab' ||
        e.key === 'Shift' ||
        e.key === 'Alt' ||
        e.key === 'Control' ||
        e.key === 'Meta' ||
        e.ctrlKey ||
        e.metaKey;

      if (!allowed) {
        if (e.key.length === 1) e.preventDefault();
      }
    };

  const handleChange =
    (i: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
      if (disabled) return;

      const raw = e.target.value ?? '';

      const normalized = normalizeDigits(raw);
      if (normalized.length > 1) {
        setDigitsFromIndex(i, normalized);
        return;
      }

      const digitChar = normalizeDigits(raw, 1);
      setDigitAt(i, digitChar);

      if (digitChar)
        focusCell(Math.min(i + 1, OTP_LENGTH - 1), { select: true });
    };

  const handlePaste =
    (i: number) => (e: React.ClipboardEvent<HTMLInputElement>) => {
      e.preventDefault();
      setDigitsFromIndex(i, e.clipboardData.getData('text'));
    };

  const handleFocus = (i: number) => () => {
    focusCell(i, { select: true });
  };

  return (
    <div className={clsx(styles.container, className)}>
      <Field title={i18n.t('activation_modal_field_title')} error={errorText}>
        <div className={styles.row}>
          {digits.map((d, i) => (
            <div key={i} className={styles.cell}>
              <Input
                ref={(el) => (inputRefs.current[i] = el)}
                value={d}
                disabled={disabled}
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={1}
                autoComplete={i === 0 ? 'one-time-code' : 'off'}
                className={styles.input}
                errorText={errorText ? SILENT_ERROR : undefined}
                onFocus={handleFocus(i)}
                onKeyDown={handleKeyDown(i)}
                onChange={handleChange(i)}
                onPaste={handlePaste(i)}
              />
            </div>
          ))}
        </div>
      </Field>
    </div>
  );
};
